import React, { useEffect, useRef, useState } from "react";
import Line from "../../component/ui/Line";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { Play } from "lucide-react";

gsap.registerPlugin(ScrollTrigger);

const OfferingsSection = () => {
  const sectionRef = useRef(null);
  const videoRef = useRef(null);

  // 1. Video Configuration
  const [showVideo, setShowVideo] = useState(false);
  const videoId = "9AL6Km6rcX0";

  // YouTube Thumbnail URL (Highest Resolution)
  const thumbnailUrl = `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`;
  // Fallback URL (Standard High Quality)
  const fallbackUrl = `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`;

  useEffect(() => {
    if (!sectionRef.current) return;

    const ctx = gsap.context(() => {
      // Text Reveal Animation
      const textElements = gsap.utils.toArray(
        sectionRef.current.querySelectorAll(".of-animated-text"),
      );
      textElements.forEach((element) => {
        gsap.set(element, {
          y: 25,
          // opacity: 0,
          
          clipPath: "inset(100% 0 0% 0)",
        });
        gsap.to(element, {
          y: 0,
          // opacity: 1,
          clipPath: "inset(0% 0 0% 0)",
          duration: 1,
          ease: "power2.out",
          scrollTrigger: {
            trigger: element.closest(".of-animate-trigger") || element,
            start: "top 85%",
            end: "center 60%",
            scrub: 1,
          },
        });
      });

      // Video Player Animation (Scale & Reveal)
      if (videoRef.current) {
        gsap.fromTo(
          videoRef.current,
          {
            scale: 0.9,
            // opacity: 0
          },
          {
            scale: 1,
            // opacity: 1,
            duration: 1.5,
            ease: "power3.out",
            scrollTrigger: {
              trigger: videoRef.current,
              start: "top 90%",
              end: "top 50%",
              scrub: 1,
            },
          },
        );
      }

      // Divider Line Animations
      const lineElements =
        sectionRef.current.querySelectorAll(".of-divider-line");
      lineElements.forEach((line) => {
        gsap.set(line, { scaleX: 0, transformOrigin: "left" });
        gsap.to(line, {
          scaleX: 1,
          scrollTrigger: {
            trigger: line.closest(".of-subtitle-wrapper") || line,
            start: "top 85%",
            scrub: 1,
          },
        });
      });

      // Card Animations
      const cardElements = sectionRef.current.querySelectorAll(".of-card");
      cardElements.forEach((card) => {
        gsap.set(card, {
          y: 40,
          // opacity: 0
        });
        gsap.to(card, {
          y: 0,
          // opacity: 1,
          scrollTrigger: {
            trigger: card,
            start: "top 85%",
            scrub: 1,
          },
        });
      });
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  return (
    <section
      ref={sectionRef}
      className="w-full bg-secondary-overlay mt-[40px] 2xl:mt-[80px] py-[39px] 2xl:py-[78px] font-[Sora]"
    >
      <div className="w-full flex flex-col gap-[30px] 2xl:gap-[60px] justify-center items-center max-w-[1612px] mx-auto">
        {/* Section Header */}
        <div className="max-w-container mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <div className="flex flex-col gap-[11px] 2xl:gap-[22px] justify-start items-start w-full">
            <div className="flex flex-row justify-start items-center w-full of-subtitle-wrapper overflow-hidden of-animate-trigger">
              <span className="text-[16px] 2xl:text-[24px] font-normal text-text-primary of-animated-text">
                Offering
              </span>
              <Line
                className="ml-[15px] 2xl:ml-[30px] bg-text-primary of-divider-line"
                width="100px"
                height="2px"
              />
            </div>
            <div className="overflow-hidden of-animate-trigger">
              <h2 className="text-[30px] 2xl:text-[60px] font-bold text-text-primary of-animated-text">
                Corporate Video
              </h2>
            </div>
            <div className="overflow-hidden of-animate-trigger">
              <p className="text-[16px] 2xl:text-[24px] font-normal leading-lg text-justify text-text-primary w-full of-animated-text">
                We envision a greener future where agriculture and energy
                sectors thrive in harmony with nature. By leveraging
                cutting-edge technologies and natural resources, we aim to
                create a sustainable ecosystem that benefits farmers,
                industries, and the environment globally.
              </p>
            </div>
          </div>
        </div>

        {/* UPDATED: YouTube Video Section with Dynamic Thumbnail */}
        <div className="w-full px-4 sm:px-6 lg:px-8 max-w-container mx-auto">
          <div
            ref={videoRef}
            className="relative w-full aspect-video rounded-[20px] 2xl:rounded-[40px] overflow-hidden shadow-2xl bg-black group"
          >
            {!showVideo ? (
              <div
                className="relative w-full h-full cursor-pointer"
                onClick={() => setShowVideo(true)}
              >
                <img
                  src={thumbnailUrl}
                  alt="Video Thumbnail"
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                  // If maxresdefault.jpg fails (returns 404), use the fallback
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = fallbackUrl;
                  }}
                />
                {/* Play Button Overlay */}
                <div className="absolute inset-0 flex items-center justify-center bg-black/20 group-hover:bg-black/40 transition-all duration-300">
                  <div className="w-[50px] h-[50px] sm:w-[80px] sm:h-[80px] bg-white/30 backdrop-blur-md rounded-full flex items-center justify-center text-white border border-white/50 transform group-hover:scale-110 transition-transform">
                    <Play size={32} fill="white" className="ml-1" />
                  </div>
                </div>
              </div>
            ) : (
              <iframe
                className="absolute top-0 left-0 w-full h-full"
                src={`https://www.youtube.com/embed/${videoId}?autoplay=1&rel=0&modestbranding=1`}
                title="Asia's Largest Biogas Plant"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
              ></iframe>
            )}
          </div>
        </div>

        {/* Offerings Grid */}
        <div className="max-w-container mx-auto px-4 sm:px-6 lg:px-8 w-full">
          <div className="flex flex-col md:flex-row gap-[25px] 2xl:gap-[50px] w-full">
            {/* Organic Fertilizers Card */}
            <div className="flex flex-col justify-start items-center w-full of-card">
              <img
                src="/images/aboutImg/aboutCard1.png"
                alt="Organic Fertilizers"
                className="w-full h-[293px] 2xl:h-[586px] object-cover rounded-[15px] 2xl:rounded-xl"
              />
              <div className="w-full bg-[#1C233A] rounded-[15px] 2xl:rounded-xl px-[15px] 2xl:px-[30px] py-[28px] -mt-[40px] 2xl:-mt-[80px] z-10">
                <h3 className="text-[20px] 2xl:text-[40px] font-bold text-text-secondary">
                  Organic Fertilizers
                </h3>
                <p className="text-[16px] 2xl:text-[24px] text-text-secondary mt-2">
                  We produce high-quality organic fertilizers made from
                  processed organic waste, designed to naturally improve soil
                  fertility.
                </p>
              </div>
            </div>

            {/* Bio Gas Energy Solutions Card */}
            <div className="flex flex-col justify-start items-center w-full of-card">
              <img
                src="/images/Bottom.webp"
                alt="Bio Gas"
                className="w-full h-[293px] 2xl:h-[586px] object-cover rounded-[15px] 2xl:rounded-xl"
              />
              <div className="w-full bg-[#1C233A] rounded-[15px] 2xl:rounded-xl px-[15px] 2xl:px-[30px] py-[28px] -mt-[40px] 2xl:-mt-[80px] z-10">
                <h3 className="text-[20px] 2xl:text-[40px] font-bold text-text-secondary">
                  Bio Gas Energy Solutions
                </h3>
                <p className="text-[16px] 2xl:text-[24px] text-text-secondary mt-2">
                  Our biogas solutions convert organic waste into clean,
                  renewable energy through advanced anaerobic digestion
                  technology.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default OfferingsSection;
